import { create } from 'zustand';
import {
  signOut,
  GoogleAuthProvider,
  signInWithPopup
} from 'firebase/auth';
import { auth } from '../lib/firebase';
import type { User, Organization } from '../types';

interface AuthState {
  currentUser: User | null;
  currentOrganization: Organization | null;
  isAuthenticated: boolean;
  error: string | null;
  isLoading: boolean;
}

interface AuthStore extends AuthState {
  setAuthState: (state: Partial<AuthState>) => void;
  signInWithGoogle: () => Promise<void>;
  logout: () => Promise<void>;
  clearError: () => void;
  redirectToApp: (path?: string) => void;
}

const useAuthStore = create<AuthStore>((set) => ({
  currentUser: null,
  currentOrganization: null,
  isAuthenticated: false,
  error: null,
  isLoading: false,

  setAuthState: (state) => set(state),

  clearError: () => set({ error: null }),

  signInWithGoogle: async () => {
    set({ isLoading: true, error: null });

    try {
      const provider = new GoogleAuthProvider();
      await signInWithPopup(auth, provider);
      // Auth state will be updated via the listener in useInitializeApp
    } catch (error: any) {
      console.error('Google Sign-In Error:', error);
      set({
        error: 'Erreur lors de la connexion avec Google.',
        isLoading: false,
      });
    } finally {
      set({ isLoading: false });
    }
  },

  logout: async () => {
    set({ isLoading: true });
    await signOut(auth);
    
    // Also try to logout from app domain
    try {
      await fetch('https://app.menioo.com/auth/logout', {
        method: 'POST',
        credentials: 'include',
      });
    } catch (error) {
      console.log('Error logging out from app domain:', error);
    }
    
    set({
      currentUser: null,
      currentOrganization: null,
      isAuthenticated: false,
      error: null,
      isLoading: false,
    });
  },
  
  redirectToApp: (path = '') => {
    window.location.href = `https://app.menioo.com${path}`;
  }
}));

export { useAuthStore };